import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionItem,
  AppButton,
  Box,
  costFormater,
  GagueChart,
  Inscription,
  SelectDropdown,
  Text,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { useAuthStore } from '@/modules/user';

import EmbeddedOnlySpendTracker from './EmbeddedOnlySpendTracker';
import { useSpendTrackerLogic } from './hooks';
import SpendTrackerUsersList from './SpendTrackerUsersList';
import TierPeriodsTable from './TierPeriodsTable';

const AccordionWrapper = styled(Box)`
  transition: all 0.15s;
  > div {
    width: 100%;
  }
`;
interface Props {
  isIndividual?: boolean;
  defaultHealthPlanYearId?: string;
  isDetailsPageMode?: boolean;
  onHealthPlanNameChange?: (healthPlanName: string) => void;
}
const SpendTracker: React.FC<Props> = ({
  isIndividual,
  isDetailsPageMode,
  defaultHealthPlanYearId,
  onHealthPlanNameChange,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setExpanded] = useState(false);
  const { user: currentUser } = useAuthStore();
  const currentUserId = useMemo(() => currentUser?.employee?.id, [currentUser]);

  const {
    formattedData,
    selectedHealthPlanYearId,
    setHealthPlanYearId,
    healthPlanYearOptions,
    isPlanYearsLoading,
    handleOpenTrackerDetails,
    chartValues,
    chartTiersValues,
    remaining,
    total,
    isLoading,
    users,
    individualTotal,
    formattedTiersData,
    coverageLevel,
    isDeductiblesLoading,
    isEmbeddedOnly,
  } = useSpendTrackerLogic({
    currentUserId,
    isIndividual,
    defaultHealthPlanYearId,
    onHealthPlanNameChange,
  });
  // const handleHover = useCallback((id: string) => () => {
  //   // setFocusedUserId(id);
  // }, []);

  // const handleBlur = useCallback(() => {
  //   // setFocusedUserId('');
  // }, []);

  if (!isLoading && !total && !isEmbeddedOnly) {
    return null;
  }

  const isEmbeddedOnlyView = !isLoading && total <= 0 && isEmbeddedOnly;

  return (
    <Box background="module" round="moduleRound" pad="l" margin={{ top: 'l', bottom: 'l' }} fill>
      <Box direction="row" align="center" justify="between">
        <Text size="large" weight={700}>
          {t('Spend Tracker')}
        </Text>
        <Box>
          {!isIndividual && (
            <Box direction="row" gap="m">
              {formattedData?.length ? (
                <Box width="200px" margin={{ left: 'spacing8' }}>
                  <SelectDropdown
                    id="healthPlanYears"
                    testId="health-plan-years"
                    name="healthPlanYears"
                    isLoading={isPlanYearsLoading}
                    singleMode
                    activeTitle
                    options={healthPlanYearOptions}
                    value={selectedHealthPlanYearId}
                    onChange={setHealthPlanYearId}
                  />
                </Box>
              ) : null}
              {!isDetailsPageMode && (
                <AppButton
                  buttonType="secondary"
                  type="submit"
                  testId="open-spend-tracker-details"
                  onClick={handleOpenTrackerDetails}
                  width="120px"
                >
                  {t('More Info')}
                </AppButton>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box margin={{ top: 'l' }} direction="row">
        {isEmbeddedOnlyView ? (
          <EmbeddedOnlySpendTracker
            coverageLevelLabel={coverageLevel}
            users={users}
            selectedHealthPlanYearId={selectedHealthPlanYearId}
            individualTotal={individualTotal}
            userId={currentUserId}
          />
        ) : (
          <>
            <Box
              elevation="default"
              fill
              width={{ max: '360px' }}
              round="container1Round"
              background="canvas"
            >
              <GagueChart items={chartValues} tiers={chartTiersValues}>
                <Box direction="column" align="center" justify="end" margin={{ top: 'auto' }}>
                  <Text weight={400} color="textSecondary">
                    {t('Remaining')}
                  </Text>
                  <Text size="2xl" weight={700}>
                    {costFormater(remaining, { isPrecision: true })}
                  </Text>
                  <Text>
                    {t('of')} {costFormater(total, { isPrecision: true })}
                  </Text>
                </Box>
              </GagueChart>
            </Box>
            <Box
              direction="column"
              elevation="default"
              round="container1Round"
              fill
              background="canvas"
              style={{ height: 'auto' }}
              margin={{ left: 'm' }}
              pad="l"
            >
              <Inscription weight="bold" margin={{ bottom: 'xs' }}>
                {`Coverage level: ${coverageLevel || ''}`}
              </Inscription>
              <Box background="module" round="container1Round" pad="s">
                <Box direction="row" round="container1Round" background="canvas" pad="s">
                  <Box direction="row" width="50%">
                    <Text size="large">
                      {isIndividual ? t('Individual Deductible:') : t('Family Deductible:')}
                    </Text>
                    <Inscription
                      size="large"
                      margin={{ left: 'xs' }}
                      weight={700}
                      whiteSpace="nowrap"
                      hasEllipsisMode
                    >
                      {costFormater(total, { isPrecision: true })}
                    </Inscription>
                  </Box>
                  <Box direction="row" width="50%">
                    <Text size="large">{t('Remaining:')}</Text>
                    <Inscription
                      size="large"
                      margin={{ left: 'xs' }}
                      weight={700}
                      whiteSpace="nowrap"
                      hasEllipsisMode
                    >
                      {costFormater(remaining, { isPrecision: true })}
                    </Inscription>
                  </Box>
                </Box>
              </Box>
              {!isIndividual && (
                <SpendTrackerUsersList
                  users={users}
                  disabled={!individualTotal}
                  selectedHealthPlanYearId={selectedHealthPlanYearId}
                  userId={currentUserId}
                />
              )}
            </Box>
          </>
        )}
      </Box>
      {!isEmbeddedOnlyView && (
        <AccordionWrapper
          pad="m"
          direction="row"
          justify="between"
          align="center"
          background="canvas"
          round="container1Round"
          margin={{ top: 'm' }}
        >
          <AccordionItem
            onChange={() => setExpanded(!isExpanded)}
            testId="tiers-module-item"
            iconSize="24px"
            isExpanded={isExpanded}
            text={
              <Inscription
                weight={400}
                margin={{ left: 'spacing4' }}
                color="textBody"
                size="16px"
                lineHeight="22px"
              >
                {t('Tiers coverage periods')}
              </Inscription>
            }
            isLeftArrow
          >
            <TierPeriodsTable formattedTiers={formattedTiersData} isLoading={isDeductiblesLoading} />
          </AccordionItem>
        </AccordionWrapper>
      )}
    </Box>
  );
};

export default React.memo(SpendTracker);
